import { useEffect, useState } from "react";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { CartEntry } from "../lib/cartStore";
import { formatCents, isVceSchoolLicence } from "../lib/products";
import type { ContactDetails, OrderTotals } from "../lib/types";

interface InvoiceConfirmationState {
  agreementNumber: string;
  paymentMethod: "invoice";
  school: { acaraId?: string; name: string };
  contact: ContactDetails;
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
  entries: CartEntry[];
  totals: OrderTotals;
}

interface CardVerifyResponse {
  agreementNumber: string;
  stripeSessionId: string;
  stripePaymentIntentId?: string;
  paymentStatus: "paid";
  school: { name: string; acaraId?: string; isManualEntry: boolean };
  contact: ContactDetails;
  purchaseOrderNumber?: string;
  items: Array<{
    name: string;
    quantity: number;
    unitAmountCents: number;
    totalAmountCents: number;
  }>;
  totals: {
    subtotalCents: number;
    gstCents: number;
    ccSurchargeCents: number;
    totalCents: number;
  };
}

export function ConfirmationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const sessionId = searchParams.get("session_id");
  const locationState = location.state as InvoiceConfirmationState | undefined;

  const [loading, setLoading] = useState(!!sessionId);
  const [error, setError] = useState<string | null>(null);
  const [cardData, setCardData] = useState<CardVerifyResponse | null>(null);

  // Verify Stripe session for card payments
  useEffect(() => {
    if (!sessionId) return;

    async function verifyPayment() {
      try {
        const response = await fetch(`/api/direct/verify?session_id=${sessionId}`);
        if (!response.ok) {
          throw new Error("Failed to verify payment");
        }
        const data = await response.json();
        setCardData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to verify payment");
      } finally {
        setLoading(false);
      }
    }

    verifyPayment();
  }, [sessionId]);

  // Redirect if no data
  if (!sessionId && !locationState) {
    navigate("/", { replace: true });
    return null;
  }

  if (loading) {
    return (
      <div className="order-page">
        <div className="container">
          <div className="card confirmation-loading">
            <p>Verifying your payment...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="order-page">
        <div className="container">
          <div className="card error-page">
            <h1>Error</h1>
            <p>{error}</p>
            <button className="btn btn-primary" onClick={() => navigate("/")}>
              Return to shop
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Determine data source (card payment or invoice)
  const isCardPayment = !!cardData;
  const agreementNumber = cardData?.agreementNumber || locationState?.agreementNumber || "";
  const schoolName = cardData?.school.name || locationState?.school.name || "";

  // Build items list - normalize from either source
  const items: Array<{ productName: string; quantity: number; totalCents: number; isSchoolLicence?: boolean }> =
    cardData?.items.map((item) => ({
      productName: item.name,
      quantity: item.quantity,
      totalCents: item.totalAmountCents,
    })) || locationState?.entries.map((entry) => ({
      productName: `${entry.group.name}${entry.variantId ? ` — ${entry.variantId.charAt(0).toUpperCase() + entry.variantId.slice(1)}` : ""}`,
      quantity: entry.quantity,
      totalCents: 0,
      // Invoice orders confirm from the cart entry, so a VCE licence reads as one licence, not 99 teachers.
      isSchoolLicence: isVceSchoolLicence(entry.group),
    })) || [];

  // Normalize totals - API uses different field names than cart
  const totals = cardData?.totals ? {
    subtotalExGstCents: cardData.totals.subtotalCents,
    gstCents: cardData.totals.gstCents,
    ccSurchargeCents: cardData.totals.ccSurchargeCents || 0,
    totalIncGstCents: cardData.totals.totalCents,
  } : locationState?.totals || {
    subtotalExGstCents: 0,
    gstCents: 0,
    ccSurchargeCents: 0,
    totalIncGstCents: 0,
  };

  return (
    <div className="order-page">
      <div className="container">
        <div className="card confirmation-page">
          <div className="confirmation-icon">
            {isCardPayment ? "✓" : "✉"}
          </div>

          <h1 className="confirmation-title">
            {isCardPayment ? "Many thanks for your order!" : "Thank you for your order!"}
          </h1>

          <p className="confirmation-message">
            {isCardPayment
              ? "Your invoice (marked paid) along with any further instructions for your product(s) will be sent shortly."
              : "We'll get to processing your order right away, and will shortly send you an invoice and welcome instructions!"}
          </p>

          {!isCardPayment && (
            <p className="confirmation-help">
              Please don't hesitate to contact us if you have any issues at{" "}
              <a href="mailto:support@smartermaths.com.au">support@smartermaths.com.au</a>
              {" "}or phone <a href="tel:1800371077">1800 371 077</a>.
            </p>
          )}

          <div className="confirmation-divider" />

          <div className="confirmation-details">
            <div className="confirmation-reference">
              <span className="confirmation-label">Agreement</span>
              <span className="confirmation-value">{agreementNumber}</span>
            </div>

            <div className="confirmation-reference">
              <span className="confirmation-label">School</span>
              <span className="confirmation-value">{schoolName}</span>
            </div>
          </div>

          <div className="confirmation-order-summary">
            {items.map((item, index) => (
              <div key={index} className="confirmation-item">
                <span className="confirmation-item-name">{item.productName}</span>
                <span className="confirmation-item-detail">
                  {item.isSchoolLicence
                    ? "1 school licence"
                    : `${item.quantity} ${item.quantity === 1 ? "teacher" : "teachers"}`}
                </span>
              </div>
            ))}

            <div className="confirmation-totals">
              <div className="confirmation-total-row">
                <span>Subtotal</span>
                <span>{formatCents(totals.subtotalExGstCents)}</span>
              </div>
              <div className="confirmation-total-row">
                <span>GST (10%)</span>
                <span>{formatCents(totals.gstCents)}</span>
              </div>
              {totals.ccSurchargeCents > 0 && (() => {
                // Split the surcharge into ex-GST and GST components
                const feeExGstCents = Math.round(totals.ccSurchargeCents / 1.1);
                const feeGstCents = totals.ccSurchargeCents - feeExGstCents;
                return (
                  <>
                    <div className="confirmation-total-row">
                      <span>Card fee (1.9%)</span>
                      <span>{formatCents(feeExGstCents)}</span>
                    </div>
                    <div className="confirmation-total-row">
                      <span>GST on card fee</span>
                      <span>{formatCents(feeGstCents)}</span>
                    </div>
                  </>
                );
              })()}
              <div className="confirmation-total-row confirmation-total-final">
                <span>Total</span>
                <span>{formatCents(totals.totalIncGstCents)}</span>
              </div>
            </div>
          </div>

          <div className="confirmation-contact">
            <p>
              Questions? Contact{" "}
              <a href="mailto:accounts@smartered.com.au">accounts@smartered.com.au</a>
              <br />
              or phone <a href="tel:1800371077">1800 371 077</a>
            </p>
          </div>

          <button className="btn btn-primary btn-full" onClick={() => navigate("/")}>
            Browse more products
          </button>
        </div>
      </div>
    </div>
  );
}
